// These are just some predefined categories, but we can use any string as category name
export const Category = {
    Navigation: 'navigation',
    System: 'system',
    SSE: 'sse',
    Events: 'events',
    Popup: 'popup',
    Dialog: 'dialog',
    Icon: 'icon',
    ListData: 'list-data',
    Formula: 'formula',
    FormulaAST: 'formula-ast',
    FormulaTouch: 'formula-touch',
    FormulaEval: 'formula-eval',
    SheetController: 'sheet-controller',
    Mesh: 'mesh',
    Slot: 'slot',
    Multiplayer: 'multiplayer'
};

// this should be @deprecated in favor of Category above
export const area = Category;

export enum LogLevel
{
    Debug = 200,
    Info = 400,
    Warn = 800,
    Error = 1000,
    Mute = 1200
}

export interface CategoryLogger
{
    debug(msg:string, ...optionalParams:any[]);

    info(msg:string, ...optionalParams:any[]);

    warn(msg:string, ...optionalParams:any[]);

    error(msg:string, ...optionalParams:any[]);

    debugGroup(groupTitle?:string, ...optionalParams:any[]);

    infoGroup(groupTitle?:string, ...optionalParams:any[]);

    warnGroup(groupTitle?:string, ...optionalParams:any[]);

    errorGroup(groupTitle?:string, ...optionalParams:any[]);

    debugGroupEnd();

    infoGroupEnd();

    warnGroupEnd();

    errorGroupEnd();

    timer(label?:string)

    timerPeek(label?:string):number

    timerReset(label?:string):number

    timerEnd(label?:string):number
}

export interface LoggingConfig
{
    [categoryName:string]:string
}

export interface Logger
{
    getLogger(categoryName:string):CategoryLogger;

    configure(loggingConfig:LoggingConfig):void;

    debug(category:string, msg:string, ...optionalParams:any[]);

    info(category:string, msg:string, ...optionalParams:any[]);

    warn(category:string, msg:string, ...optionalParams:any[]);

    error(category:string, msg:string, ...optionalParams:any[]);
}

class CategoryLoggerImpl implements CategoryLogger
{
    private readonly categoryName:string;

    private readonly timers:Map<string, number> = new Map<string, number>();

    private logLevel:LogLevel;

    constructor(categoryName:string, logLevel:LogLevel)
    {
        this.categoryName = categoryName;
        this.logLevel = logLevel;
    }

    setLogLevel(logLevel:LogLevel)
    {
        this.logLevel = logLevel;
    }

    debug(msg:string, ...optionalParams:any[])
    {
        this.message(LogLevel.Debug, msg, ...optionalParams);
    }

    error(msg:string, ...optionalParams:any[])
    {
        this.message(LogLevel.Error, msg, ...optionalParams);
    }

    info(msg:string, ...optionalParams:any[])
    {
        this.message(LogLevel.Info, msg, ...optionalParams);
    }

    warn(msg:string, ...optionalParams:any[])
    {
        this.message(LogLevel.Warn, msg, ...optionalParams);
    }

    debugGroup(groupTitle?:string, ...optionalParams:any[])
    {
        this.group(LogLevel.Debug, groupTitle, ...optionalParams);
    }

    infoGroup(groupTitle?:string, ...optionalParams:any[])
    {
        this.group(LogLevel.Info, groupTitle, ...optionalParams);
    }

    warnGroup(groupTitle?:string, ...optionalParams:any[])
    {
        this.group(LogLevel.Warn, groupTitle, ...optionalParams);
    }

    errorGroup(groupTitle?:string, ...optionalParams:any[])
    {
        this.group(LogLevel.Error, groupTitle, ...optionalParams);
    }

    debugGroupEnd()
    {
        this.groupEnd(LogLevel.Debug);
    }

    infoGroupEnd()
    {
        this.groupEnd(LogLevel.Info);
    }

    warnGroupEnd()
    {
        this.groupEnd(LogLevel.Warn);
    }

    errorGroupEnd()
    {
        this.groupEnd(LogLevel.Error);
    }

    private groupEnd(msgLevel:LogLevel)
    {
        if( msgLevel < this.logLevel )
            return;

        console.groupEnd();
    }

    private group(msgLevel:LogLevel, groupTitle?:string, ...optionalParams:any[])
    {
        if( msgLevel < this.logLevel )
            return;

        console.group(groupTitle, ...optionalParams);
    }

    private message(msgLevel:LogLevel, msg:string, ...optionalParams:any[])
    {
        if( msgLevel < this.logLevel )
            return;

        switch( msgLevel )
        {
            case LogLevel.Debug:
                console.log(msg, ...optionalParams);
                break;
            case LogLevel.Info:
                console.info(msg, ...optionalParams);
                break;
            case LogLevel.Warn:
                console.warn(msg, ...optionalParams);
                break;
            case LogLevel.Error:
                console.error(msg, ...optionalParams);
                break;
        }
    }

    timer(label:string = 'global')
    {
        this.timers.set(label, performance.now());
    }

    timerPeek(label:string = 'global'):number
    {
        return this._calcTimer(label);
    }

    timerReset(label:string = 'global'):number
    {
        return this._calcTimer(label, (prev, curr) => this.timers.set(label, curr));
    }

    timerEnd(label:string = 'global'):number
    {
        return this._calcTimer(label, () => this.timers.delete(label));
    }

    private _calcTimer(label:string, adjusterFn?:(prev:number, curr:number) => void):number
    {
        const prevValue = this.timers.get(label);
        if( prevValue == null )
            return 0;

        const currValue = performance.now();
        adjusterFn && adjusterFn(prevValue, currValue);
        return currValue - prevValue;
    }
}

class LoggerImpl implements Logger
{
    private defaultLevel:LogLevel = LogLevel.Error;
    private categoryLevels:Map<string, LogLevel> = new Map<string, LogLevel>();
    private categoryLoggers:Map<string, CategoryLogger> = new Map<string, CategoryLogger>();

    getLogger(categoryName:string):CategoryLogger
    {
        let categoryLogger = this.categoryLoggers.get(categoryName);
        if( !categoryLogger )
        {
            categoryLogger = new CategoryLoggerImpl(
                categoryName,
                this.categoryLevels.get(categoryName) || this.defaultLevel
            );
            this.categoryLoggers.set(categoryName, categoryLogger);
        }

        return categoryLogger;
    }

    configure(loggingConfig:LoggingConfig):void
    {
        this.defaultLevel = this._parseLevelName(loggingConfig['default'] || 'error');
        Object.entries(loggingConfig).forEach(([categoryName, levelName]) =>
            this.categoryLevels.set(categoryName, this._parseLevelName(levelName as string))
        );

        this.categoryLoggers.forEach((categoryLogger, categoryName) =>
            (categoryLogger as CategoryLoggerImpl).setLogLevel(
                this.categoryLevels.get(categoryName) || this.defaultLevel
            )
        );
    }

    private _parseLevelName(levelName:string = 'error'):LogLevel
    {
        switch( levelName.toLowerCase() )
        {
            case 'debug':
                return LogLevel.Debug;
            case 'info':
                return LogLevel.Info;
            case 'warn':
                return LogLevel.Warn;
            case 'error':
                return LogLevel.Error;
            case 'mute':
                return LogLevel.Mute;
            default:
                return LogLevel.Error;
        }
    }

    debug(category:string, msg:string, ...optionalParams:any[])
    {
        this.getLogger(category).debug(msg, ...optionalParams);
    }

    error(category:string, msg:string, ...optionalParams:any[])
    {
        this.getLogger(category).error(msg, ...optionalParams);
    }

    info(category:string, msg:string, ...optionalParams:any[])
    {
        this.getLogger(category).info(msg, ...optionalParams);
    }

    warn(category:string, msg:string, ...optionalParams:any[])
    {
        this.getLogger(category).warn(msg, ...optionalParams);
    }
}

export const Logger = new LoggerImpl();
export const mk = Logger;
